import { useCallback, useMemo, useState } from "react";

export const SCHEDULE_TYPES = [
    { id: "today", label: "Today" },
    { id: "everyday", label: "Everyday" },
    { id: "weekdays", label: "Weekdays" },
    { id: "custom", label: "Custom" },
];

export const WEEKDAY_OPTIONS = [
    { id: 0, label: "Mon" },
    { id: 1, label: "Tue" },
    { id: 2, label: "Wed" },
    { id: 3, label: "Thu" },
    { id: 4, label: "Fri" },
    { id: 5, label: "Sat" },
    { id: 6, label: "Sun" },
];

const WEEKDAY_LABELS = WEEKDAY_OPTIONS.map((d) => d.label);

export const DEFAULT_SCHEDULE_STATE = {
    deliveryMode: "now",
    scheduleType: "today",
    time: "09:00",
    customWeekdays: [0, 1, 2, 3, 4],
    customDate: "",
    customRecurring: true,
};

function parseTime(timeStr) {
    const [h, m] = (timeStr || "09:00").split(":").map((n) => parseInt(n, 10));
    return { hours: Number.isFinite(h) ? h : 9, minutes: Number.isFinite(m) ? m : 0 };
}

function setLocalTime(date, timeStr) {
    const { hours, minutes } = parseTime(timeStr);
    const d = new Date(date);
    d.setHours(hours, minutes, 0, 0);
    return d;
}

function startOfToday() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
}

function formatTimeLabel(timeStr) {
    const d = setLocalTime(new Date(), timeStr);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function formatDateLabel(dateStr) {
    if (!dateStr) return "";
    const d = new Date(`${dateStr}T12:00:00`);
    return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

export function computeFirstScheduledAt(schedule) {
    const now = new Date();
    const { scheduleType, time, customWeekdays, customDate, customRecurring } = schedule;

    if (scheduleType === "today") {
        const candidate = setLocalTime(startOfToday(), time);
        return candidate > now ? candidate : null;
    }

    if (scheduleType === "everyday") {
        let candidate = setLocalTime(startOfToday(), time);
        if (candidate <= now) {
            candidate = new Date(candidate);
            candidate.setDate(candidate.getDate() + 1);
        }
        return candidate;
    }

    if (scheduleType === "weekdays") {
        for (let offset = 0; offset < 14; offset += 1) {
            const day = new Date(startOfToday());
            day.setDate(day.getDate() + offset);
            const wd = day.getDay() === 0 ? 6 : day.getDay() - 1;
            if (wd >= 0 && wd <= 4) {
                const candidate = setLocalTime(day, time);
                if (candidate > now) return candidate;
            }
        }
        return null;
    }

    if (scheduleType === "custom") {
        if (!customRecurring) {
            if (!customDate) return null;
            const candidate = setLocalTime(new Date(`${customDate}T00:00:00`), time);
            return candidate > now ? candidate : null;
        }
        const days = [...(customWeekdays || [])].sort((a, b) => a - b);
        if (!days.length) return null;
        for (let offset = 0; offset < 366; offset += 1) {
            const day = new Date(startOfToday());
            day.setDate(day.getDate() + offset);
            const wd = day.getDay() === 0 ? 6 : day.getDay() - 1;
            if (days.includes(wd)) {
                const candidate = setLocalTime(day, time);
                if (candidate > now) return candidate;
            }
        }
    }

    return null;
}

export function validateDutyCheckSchedule(schedule) {
    if (schedule.deliveryMode !== "scheduled") {
        return null;
    }

    const { scheduleType, time, customWeekdays, customDate, customRecurring } = schedule;

    if (!time || !/^\d{2}:\d{2}$/.test(time)) {
        return "Please select a valid time.";
    }

    const { hours, minutes } = parseTime(time);
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        return "Please select a valid time.";
    }

    if (scheduleType === "today") {
        if (!computeFirstScheduledAt(schedule)) {
            return "Selected time must be later today.";
        }
        return null;
    }

    if (scheduleType === "everyday" || scheduleType === "weekdays") {
        if (!computeFirstScheduledAt(schedule)) {
            return "Unable to compute the next run time.";
        }
        return null;
    }

    if (scheduleType === "custom") {
        if (customRecurring) {
            if (!customWeekdays?.length) {
                return "Select at least one weekday for a custom recurring schedule.";
            }
            if (!computeFirstScheduledAt(schedule)) {
                return "Unable to compute the next run time for selected weekdays.";
            }
            return null;
        }
        if (!customDate) {
            return "Please select a date for this one-time schedule.";
        }
        if (!computeFirstScheduledAt(schedule)) {
            return "Selected date and time must be in the future.";
        }
        return null;
    }

    return "Invalid schedule type.";
}

export function buildSchedulePayload(schedule) {
    if (schedule.deliveryMode !== "scheduled") {
        return {
            scheduled_at: null,
            schedule_type: null,
            schedule_config: null,
        };
    }

    const firstAt = computeFirstScheduledAt(schedule);
    const schedule_config = {
        time: schedule.time,
        weekdays:
            schedule.scheduleType === "weekdays"
                ? [0, 1, 2, 3, 4]
                : schedule.scheduleType === "custom" && schedule.customRecurring
                  ? [...schedule.customWeekdays].sort((a, b) => a - b)
                  : null,
        start_date:
            schedule.scheduleType === "custom" && !schedule.customRecurring
                ? schedule.customDate
                : null,
        is_recurring:
            schedule.scheduleType === "today"
                ? false
                : schedule.scheduleType === "custom"
                  ? schedule.customRecurring
                  : true,
    };

    return {
        scheduled_at: firstAt ? firstAt.toISOString() : null,
        schedule_type: schedule.scheduleType,
        schedule_config,
    };
}

export function getScheduleSummary(schedule) {
    if (schedule.deliveryMode !== "scheduled") {
        return "Send immediately";
    }

    const timeLabel = formatTimeLabel(schedule.time);

    switch (schedule.scheduleType) {
        case "today":
            return `Today at ${timeLabel} (one-time)`;
        case "everyday":
            return `Every day at ${timeLabel}`;
        case "weekdays":
            return `Weekdays (Mon–Fri) at ${timeLabel}`;
        case "custom":
            if (schedule.customRecurring) {
                const days = (schedule.customWeekdays || [])
                    .sort((a, b) => a - b)
                    .map((id) => WEEKDAY_LABELS[id])
                    .join(", ");
                return `${days || "No days"} at ${timeLabel} (recurring)`;
            }
            return `${formatDateLabel(schedule.customDate)} at ${timeLabel} (one-time)`;
        default:
            return "Scheduled";
    }
}

export function useDutyCheckSchedule(initialState = DEFAULT_SCHEDULE_STATE) {
    const [schedule, setSchedule] = useState(initialState);

    const resetSchedule = useCallback(() => {
        setSchedule({ ...DEFAULT_SCHEDULE_STATE });
    }, []);

    const patchSchedule = useCallback((patch) => {
        setSchedule((prev) => ({ ...prev, ...patch }));
    }, []);

    const summary = useMemo(() => getScheduleSummary(schedule), [schedule]);

    const validate = useCallback(() => validateDutyCheckSchedule(schedule), [schedule]);

    const toPayload = useCallback(() => buildSchedulePayload(schedule), [schedule]);

    return {
        schedule,
        setSchedule,
        patchSchedule,
        resetSchedule,
        summary,
        validate,
        toPayload,
    };
}
